import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { CheckIcon, XMarkIcon, MapPinIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/solid';
import './GymSelector.css';
import { resolveGymSearchArea } from './gymSearchOrigin';
import { gymsRepresentSamePlace } from './gymIdentity';

// Fix Leaflet default icon issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const userGymIcon = L.divIcon({
  className: 'gym-marker-icon gym-marker-saved',
  html: '<div class="gym-marker-dot saved"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const searchGymIcon = L.divIcon({
  className: 'gym-marker-icon gym-marker-search',
  html: '<div class="gym-marker-dot search"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const activeGymIcon = L.divIcon({
  className: 'gym-marker-icon gym-marker-active',
  html: '<div class="gym-marker-dot active"></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const pinIcon = L.divIcon({
  className: 'gym-marker-icon gym-marker-pin',
  html: '<div class="gym-marker-dot pin"></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const userLocationIcon = L.divIcon({
  className: 'gym-marker-icon gym-marker-user',
  html: '<div class="gym-user-dot"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function GymSelector({
  gymDetails,
  gymConfirmed,
  userGyms,
  userLocation,
  backendService,
  onConfirmGym,
  onChangeGym,
  onClearGym,
  onCreateNewGym,
  onSelectSearchGym,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const userMarkerRef = useRef(null);
  const pinMarkerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchRequestRef = useRef(0);
  const searchAbortRef = useRef(null);

  const [nearbyGyms, setNearbyGyms] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchKeywords, setSearchKeywords] = useState('');
  const [pinMode, setPinMode] = useState(false);
  const [pinPosition, setPinPosition] = useState(null);
  const [newGymName, setNewGymName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const center = userLocation
      ? [userLocation.latitude, userLocation.longitude]
      : gymDetails?.latitude && gymDetails?.longitude
        ? [gymDetails.latitude, gymDetails.longitude]
        : [0, 0];

    const map = L.map(mapContainerRef.current, {
      center,
      zoom: 15,
      zoomControl: false,
      attributionControl: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);

    L.control.zoom({ position: 'topright' }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Handle click for pin placement
    map.on('click', (e) => {
      // Only place pin if in pin mode
      if (!pinModeRef.current) return;
      const { lat, lng } = e.latlng;
      setPinPosition({ latitude: lat, longitude: lng });
    });

    // Trigger initial nearby search
    if (userLocation) {
      searchNearby(userLocation.latitude, userLocation.longitude, 2000, '');
    }

    // Fix Leaflet rendering issues when container size changes
    setTimeout(() => {
      map.invalidateSize();
    }, 200);

    return () => {
      searchRequestRef.current += 1;
      searchAbortRef.current?.abort();
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track pinMode in a ref so the map click handler always sees the latest value
  const pinModeRef = useRef(pinMode);
  useEffect(() => {
    pinModeRef.current = pinMode;
  }, [pinMode]);

  // Update user location marker
  useEffect(() => {
    if (!mapRef.current || !userLocation) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng([userLocation.latitude, userLocation.longitude]);
    } else {
      userMarkerRef.current = L.marker(
        [userLocation.latitude, userLocation.longitude],
        { icon: userLocationIcon, zIndexOffset: 1000 }
      )
        .addTo(mapRef.current)
        .bindTooltip('You are here', { direction: 'top', offset: [0, -10] });
    }
  }, [userLocation]);

  // Update pin marker when pinPosition changes
  useEffect(() => {
    if (!mapRef.current) return;

    if (pinMarkerRef.current) {
      if (pinPosition) {
        pinMarkerRef.current.setLatLng([pinPosition.latitude, pinPosition.longitude]);
      } else {
        pinMarkerRef.current.remove();
        pinMarkerRef.current = null;
      }
    } else if (pinPosition) {
      pinMarkerRef.current = L.marker(
        [pinPosition.latitude, pinPosition.longitude],
        { icon: pinIcon, draggable: true, zIndexOffset: 900 }
      ).addTo(mapRef.current);

      pinMarkerRef.current.bindTooltip('New gym location', { direction: 'top', offset: [0, -14] });

      pinMarkerRef.current.on('dragend', () => {
        const pos = pinMarkerRef.current.getLatLng();
        setPinPosition({ latitude: pos.lat, longitude: pos.lng });
      });
    }
  }, [pinPosition]);

  // Update markers when data changes
  useEffect(() => {
    if (!mapRef.current || !markersLayerRef.current) return;
    markersLayerRef.current.clearLayers();

    // Add user's saved gym markers
    userGyms.forEach((gym) => {
      if (!gym.latitude || !gym.longitude) return;
      const isActive = gymDetails && gym._id === gymDetails._id;
      const marker = L.marker([gym.latitude, gym.longitude], {
        icon: isActive ? activeGymIcon : userGymIcon,
        zIndexOffset: isActive ? 800 : 500,
      });
      marker.bindTooltip(gym.name, {
        direction: 'top',
        offset: [0, -12],
        className: 'gym-map-tooltip',
      });
      marker.on('click', () => {
        onChangeGym(gym._id);
      });
      markersLayerRef.current.addLayer(marker);
    });

    // Add nearby search result markers
    nearbyGyms.forEach((gym) => {
      // Skip a search place already represented by a canonical or legacy saved gym
      const isSaved = userGyms.some(g => gymsRepresentSamePlace(g, gym));
      if (isSaved) return;

      const marker = L.marker([gym.latitude, gym.longitude], {
        icon: searchGymIcon,
        // Search results are actions, so keep them above the passive user dot.
        // Nearby places otherwise become visually covered and unclickable.
        zIndexOffset: 1100,
      });
      marker.bindTooltip(
        `${gym.name}${gym.distance ? ` (${Math.round(gym.distance)}m)` : ''}`,
        { direction: 'top', offset: [0, -10], className: 'gym-map-tooltip' }
      );
      marker.on('click', () => {
        onSelectSearchGym(gym);
      });
      markersLayerRef.current.addLayer(marker);
    });
  }, [userGyms, nearbyGyms, gymDetails, onChangeGym, onSelectSearchGym]);

  const searchNearby = useCallback(async (lat, lng, radius, keywords) => {
    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    const requestId = ++searchRequestRef.current;
    setIsSearching(true);
    setSearchError(null);
    try {
      const results = await backendService.searchNearbyGyms(
        lat,
        lng,
        radius,
        keywords || null,
        { signal: controller.signal }
      );
      if (requestId !== searchRequestRef.current) return;
      setNearbyGyms(results);
      setHasSearched(true);
    } catch (error) {
      if (requestId !== searchRequestRef.current || controller.signal.aborted) return;
      console.error('Error searching nearby gyms:', error);
      setNearbyGyms([]);
      setHasSearched(true);
      setSearchError(
        error.response?.data?.error || 'Gym search is temporarily unavailable. You can still place a pin on the map.'
      );
    } finally {
      if (requestId === searchRequestRef.current) {
        searchAbortRef.current = null;
        setIsSearching(false);
      }
    }
  }, [backendService]);

  const handleSearchKeywordsChange = (e) => {
    const value = e.target.value;
    const hasSearchArea = Boolean(resolveGymSearchArea(mapRef.current, userLocation));
    setSearchKeywords(value);
    searchRequestRef.current += 1;
    searchAbortRef.current?.abort();
    searchAbortRef.current = null;
    setSearchError(null);
    setIsSearching(hasSearchArea);

    // Debounce the search
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (hasSearchArea) {
      searchDebounceRef.current = setTimeout(() => {
        const area = resolveGymSearchArea(mapRef.current, userLocation);
        if (area) {
          searchNearby(area.latitude, area.longitude, area.radius, value);
        }
      }, 600);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    const area = resolveGymSearchArea(mapRef.current, userLocation);
    if (area) {
      searchNearby(area.latitude, area.longitude, area.radius, searchKeywords);
    }
  };

  const handleTogglePinMode = () => {
    if (pinMode) {
      // Exiting pin mode — clear pin
      setPinPosition(null);
      setNewGymName('');
    }
    setPinMode(!pinMode);
  };

  const handleCreatePinnedGym = () => {
    if (!pinPosition || !newGymName.trim()) return;
    onCreateNewGym(newGymName.trim(), pinPosition.latitude, pinPosition.longitude);
    setPinPosition(null);
    setNewGymName('');
    setPinMode(false);
  };

  return (
    <div className="gym-selector-dropdown">
      {/* Confirm button for auto-detected gym */}
      {gymDetails && !gymConfirmed && (
        <button className="gym-confirm-btn" onClick={onConfirmGym}>
          <CheckIcon style={{ width: 14, height: 14, marginRight: 6 }} />
          Confirm: {gymDetails.name}
        </button>
      )}

      {/* Embedded map */}
      <div className="gym-map-container">
        <div ref={mapContainerRef} className="gym-map" />
        {isSearching && (
          <div className="gym-map-loading">
            <div className="gym-map-spinner" />
          </div>
        )}
      </div>

      {/* Search keywords */}
      <form className="gym-search-form" onSubmit={handleSearchSubmit}>
        <div className="gym-search-input-wrap">
          <MagnifyingGlassIcon style={{ width: 14, height: 14 }} />
          <input
            type="text"
            value={searchKeywords}
            onChange={handleSearchKeywordsChange}
            placeholder="Search nearby places..."
            className="gym-search-input"
          />
        </div>
      </form>

      {/* Saved gyms list */}
      {userGyms.filter((g) => g._id !== gymDetails?._id).length > 0 && (
        <>
          <div className="gym-section-label">Your Gyms</div>
          <div className="gym-list">
            {userGyms
              .filter((g) => g._id !== gymDetails?._id)
              .map((gym) => (
                <button
                  key={gym._id}
                  className="gym-option"
                  onClick={() => onChangeGym(gym._id)}
                >
                  <MapPinIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                  {gym.name}
                </button>
              ))}
          </div>
        </>
      )}

      {/* Nearby search results */}
      {nearbyGyms.length > 0 && (
        <>
          <div className="gym-section-label">
            Nearby Places
            {searchKeywords && <span className="gym-search-tag"> — "{searchKeywords}"</span>}
          </div>
          <div className="gym-list nearby-gyms">
            {nearbyGyms.map((gym, idx) => {
              // Skip if already represented by a canonical or legacy saved gym
              const isSaved = userGyms.some(g => gymsRepresentSamePlace(g, gym));
              if (isSaved) return null;
              return (
                <button
                  key={gym.osm_id || idx}
                  className="gym-option search-gym"
                  onClick={() => onSelectSearchGym(gym)}
                >
                  <div className="gym-option-main">
                    <MapPinIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    <span className="gym-option-name">{gym.name}</span>
                  </div>
                  <div className="gym-option-meta">
                    <span className="gym-distance">{Math.round(gym.distance)}m away</span>
                    {gym.address && <span className="gym-address">{gym.address}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {searchError && (
        <div className="gym-search-error" role="alert">{searchError}</div>
      )}

      {hasSearched && !searchError && nearbyGyms.length === 0 && (
        <div className="gym-no-results">No nearby places found. Try different keywords or place a pin on the map.</div>
      )}

      {/* Pin mode / Add new gym */}
      <div className="gym-pin-section">
        <button
          className={`gym-pin-toggle ${pinMode ? 'active' : ''}`}
          onClick={handleTogglePinMode}
        >
          {pinMode ? (
            <>
              <XMarkIcon style={{ width: 14, height: 14, marginRight: 6 }} />
              Cancel pin placement
            </>
          ) : (
            <>
              <PlusIcon style={{ width: 14, height: 14, marginRight: 6 }} />
              Place a pin on map...
            </>
          )}
        </button>

        {pinMode && (
          <div className="gym-pin-form">
            {!pinPosition && (
              <div className="gym-pin-hint">Tap on the map to place your gym pin</div>
            )}
            {pinPosition && (
              <>
                <input
                  type="text"
                  value={newGymName}
                  onChange={(e) => setNewGymName(e.target.value)}
                  placeholder="Name this gym..."
                  className="gym-pin-name-input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newGymName.trim()) {
                      handleCreatePinnedGym();
                    }
                  }}
                />
                <button
                  className="gym-pin-save-btn"
                  onClick={handleCreatePinnedGym}
                  disabled={!newGymName.trim()}
                >
                  <CheckIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                  Save gym
                </button>
              </>
            )}
          </div>
        )}

        {/* Quick add without map pin (uses current location) */}
        {!pinMode && (
          <div className="gym-new-input">
            <input
              type="text"
              placeholder="Or type a name to add at current location..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  onCreateNewGym(e.target.value);
                  e.target.value = '';
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Clear gym */}
      {gymDetails && (
        <button className="gym-clear-btn" onClick={onClearGym}>
          <XMarkIcon style={{ width: 14, height: 14, marginRight: 6 }} />
          Clear gym
        </button>
      )}
    </div>
  );
}

export default GymSelector;
